import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, protocolVersionIdSchema, sha256DigestSchema, } from "./identifiers.js";
export const recurrenceFingerprintSchema = z
    .object({
    processId: protocolIdSchema,
    triggerDigest: sha256DigestSchema,
    sourcePolicyDigest: sha256DigestSchema,
    repositoryTopologyDigest: sha256DigestSchema,
    gateDefinitionDigest: sha256DigestSchema,
    registryVersionDigest: sha256DigestSchema,
    environmentCapabilityDigest: sha256DigestSchema,
    observedEffectsDigest: sha256DigestSchema,
    consequenceClass: z.enum(["low", "medium", "high", "critical"]),
})
    .strict();
export const recurrenceDriftDimensionSchema = z.enum([
    "trigger",
    "source_policy",
    "repository_topology",
    "gate_definition",
    "registry_version",
    "environment_capability",
    "observed_effects",
    "consequence",
]);
export const recurrenceDriftAssessmentSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    assessedAt: protocolTimestampSchema,
    processId: protocolIdSchema,
    baseFingerprintDigest: sha256DigestSchema,
    observedFingerprintDigest: sha256DigestSchema,
    relationship: z.enum(["same", "compatible", "material_drift"]),
    changedDimensions: z.array(recurrenceDriftDimensionSchema),
    compatibilityDecision: z
        .object({
        reviewerActorId: protocolIdSchema,
        reason: z.string().trim().min(1),
    })
        .strict()
        .nullable(),
    successorRequired: z.boolean(),
    confirmationRequired: z.boolean(),
    policyReviewRequired: z.boolean(),
    activeAcceptedVersionEditedInPlace: z.literal(false),
    historicalAttemptContractDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.relationship === "compatible" && value.compatibilityDecision === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["compatibilityDecision"],
            message: "compatible non-material drift requires an auditable compatibility decision",
        });
    }
    if (value.relationship === "material_drift" && !value.successorRequired) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successorRequired"],
            message: "material drift requires a successor proposal",
        });
    }
});
export const recurrenceSuccessorProposalSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    baseContractDigest: sha256DigestSchema,
    baseVersion: protocolVersionIdSchema,
    successorVersion: protocolVersionIdSchema,
    reason: z.string().trim().min(1),
    driftAssessmentId: protocolIdSchema,
    state: z.literal("PROPOSED"),
    returnsOccurrenceTo: z.enum(["confirmation", "policy_review"]),
    protectedExecutionAllowedBeforeAcceptance: z.literal(false),
    activeAcceptedVersionEditedInPlace: z.literal(false),
})
    .strict()
    .superRefine((value, context) => {
    if (value.baseVersion === value.successorVersion) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["successorVersion"],
            message: "successor proposals must advance the contract version",
        });
    }
});
export const recurrenceApplicationSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    occurrenceId: protocolIdSchema,
    driftAssessmentId: protocolIdSchema,
    state: z.enum(["REUSE_ACCEPTED_CONTRACT", "REQUIRES_CONFIRMATION", "REQUIRES_POLICY_REVIEW"]),
    attachedContractDigest: sha256DigestSchema.nullable(),
    successorProposal: recurrenceSuccessorProposalSchema.nullable(),
    canonicalVerdictState: z.enum(["ELIGIBLE", "UNKNOWN"]),
    protectedExecutionAllowed: z.boolean(),
    activeAcceptedVersionEditedInPlace: z.literal(false),
    historicalAttemptContractDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.state === "REUSE_ACCEPTED_CONTRACT" && !value.attachedContractDigest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attachedContractDigest"],
            message: "reused occurrences must attach the accepted contract",
        });
    }
    if (value.state !== "REUSE_ACCEPTED_CONTRACT" && value.canonicalVerdictState !== "UNKNOWN") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["canonicalVerdictState"],
            message: "material drift returns the occurrence to UNKNOWN",
        });
    }
    if (value.successorProposal && value.protectedExecutionAllowed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["protectedExecutionAllowed"],
            message: "successor proposals must be accepted before protected execution",
        });
    }
});
export const workflowRecurrenceMatchSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    matchedAt: protocolTimestampSchema,
    occurrenceId: protocolIdSchema,
    processId: protocolIdSchema,
    acceptedContractDigest: sha256DigestSchema,
    acceptedVersion: protocolVersionIdSchema,
    baseFingerprintDigest: sha256DigestSchema,
    observedFingerprintDigest: sha256DigestSchema,
    relationship: z.enum(["same", "possible", "different"]),
    confidence: z.number().min(0).max(1),
    driftSignals: z.array(recurrenceDriftDimensionSchema),
    confirmationRequired: z.boolean(),
    attachedContractDigest: sha256DigestSchema.nullable(),
    protectedContractInheritanceAllowed: z.boolean(),
    identityComparedAgainst: z
        .object({
        previousTaskId: protocolIdSchema.nullable(),
        currentTaskId: protocolIdSchema.nullable(),
        previousSessionId: protocolIdSchema.nullable(),
        currentSessionId: protocolIdSchema.nullable(),
        previousRepositoryId: protocolIdSchema.nullable(),
        currentRepositoryId: protocolIdSchema.nullable(),
        previousEnvironmentId: protocolIdSchema.nullable(),
        currentEnvironmentId: protocolIdSchema.nullable(),
    })
        .strict(),
    explanation: z.array(z.string().trim().min(1)).min(1),
    activeAcceptedVersionEditedInPlace: z.literal(false),
})
    .strict()
    .superRefine((value, context) => {
    if (value.relationship === "same" && value.confirmationRequired) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["confirmationRequired"],
            message: "same recurrence does not require confirmation",
        });
    }
    if (value.relationship === "same" && value.attachedContractDigest === null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attachedContractDigest"],
            message: "same recurrence must attach the accepted contract",
        });
    }
    if (value.relationship !== "same" && value.attachedContractDigest !== null) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attachedContractDigest"],
            message: "possible or different recurrence cannot silently inherit a contract",
        });
    }
    if (value.relationship !== "same" && value.protectedContractInheritanceAllowed) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["protectedContractInheritanceAllowed"],
            message: "possible or different recurrence requires confirmation before protected inheritance",
        });
    }
});
export function recurrenceFingerprintDigest(fingerprint, sha256) {
    return canonicalSha256(recurrenceFingerprintSchema.parse(fingerprint), sha256);
}
function changedDimensions(base, observed) {
    const changed = [];
    if (base.triggerDigest !== observed.triggerDigest)
        changed.push("trigger");
    if (base.sourcePolicyDigest !== observed.sourcePolicyDigest)
        changed.push("source_policy");
    if (base.repositoryTopologyDigest !== observed.repositoryTopologyDigest) {
        changed.push("repository_topology");
    }
    if (base.gateDefinitionDigest !== observed.gateDefinitionDigest)
        changed.push("gate_definition");
    if (base.registryVersionDigest !== observed.registryVersionDigest)
        changed.push("registry_version");
    if (base.environmentCapabilityDigest !== observed.environmentCapabilityDigest) {
        changed.push("environment_capability");
    }
    if (base.observedEffectsDigest !== observed.observedEffectsDigest)
        changed.push("observed_effects");
    if (base.consequenceClass !== observed.consequenceClass)
        changed.push("consequence");
    return changed;
}
function hasMaterialDrift(changed) {
    return changed.some((dimension) => [
        "source_policy",
        "gate_definition",
        "registry_version",
        "environment_capability",
        "observed_effects",
        "consequence",
    ].includes(dimension));
}
function nullableProtocolId(value) {
    return value ?? null;
}
export function matchWorkflowRecurrence(input) {
    const base = recurrenceFingerprintSchema.parse(input.base);
    const observed = recurrenceFingerprintSchema.parse(input.observed);
    const driftSignals = changedDimensions(base, observed);
    const material = hasMaterialDrift(driftSignals);
    const relationship = driftSignals.length === 0 ? "same" : material ? "different" : "possible";
    const confirmationRequired = relationship !== "same";
    const baseFingerprintDigest = recurrenceFingerprintDigest(base, input.sha256);
    const observedFingerprintDigest = recurrenceFingerprintDigest(observed, input.sha256);
    const explanation = relationship === "same"
        ? [
            "The normalized recurrence fingerprint is unchanged, so task/session/repository/environment ids are not used as workflow identity.",
        ]
        : relationship === "possible"
            ? [
                "Only non-material recurrence dimensions changed; the accepted contract cannot be inherited until a reviewer confirms compatibility.",
            ]
            : [
                "Material recurrence dimensions changed; the later occurrence is a different workflow until a successor is accepted.",
            ];
    return workflowRecurrenceMatchSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        matchedAt: input.matchedAt,
        occurrenceId: input.occurrenceId,
        processId: base.processId,
        acceptedContractDigest: input.acceptedContractDigest,
        acceptedVersion: input.acceptedVersion,
        baseFingerprintDigest,
        observedFingerprintDigest,
        relationship,
        confidence: relationship === "same" ? 0.99 : relationship === "possible" ? 0.66 : 0.05,
        driftSignals,
        confirmationRequired,
        attachedContractDigest: relationship === "same" ? input.acceptedContractDigest : null,
        protectedContractInheritanceAllowed: relationship === "same",
        identityComparedAgainst: {
            previousTaskId: nullableProtocolId(input.previousTaskId),
            currentTaskId: nullableProtocolId(input.currentTaskId),
            previousSessionId: nullableProtocolId(input.previousSessionId),
            currentSessionId: nullableProtocolId(input.currentSessionId),
            previousRepositoryId: nullableProtocolId(input.previousRepositoryId),
            currentRepositoryId: nullableProtocolId(input.currentRepositoryId),
            previousEnvironmentId: nullableProtocolId(input.previousEnvironmentId),
            currentEnvironmentId: nullableProtocolId(input.currentEnvironmentId),
        },
        explanation,
        activeAcceptedVersionEditedInPlace: false,
    });
}
export function assessRecurrenceDrift(input) {
    const base = recurrenceFingerprintSchema.parse(input.base);
    const observed = recurrenceFingerprintSchema.parse(input.observed);
    const changed = changedDimensions(base, observed);
    const material = hasMaterialDrift(changed);
    const relationship = changed.length === 0 ? "same" : material ? "material_drift" : "compatible";
    return recurrenceDriftAssessmentSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        assessedAt: input.assessedAt,
        processId: base.processId,
        baseFingerprintDigest: recurrenceFingerprintDigest(base, input.sha256),
        observedFingerprintDigest: recurrenceFingerprintDigest(observed, input.sha256),
        relationship,
        changedDimensions: changed,
        compatibilityDecision: relationship === "compatible" ? (input.compatibleReview ?? null) : null,
        successorRequired: relationship === "material_drift",
        confirmationRequired: relationship === "material_drift",
        policyReviewRequired: changed.includes("consequence"),
        activeAcceptedVersionEditedInPlace: false,
        historicalAttemptContractDigest: input.historicalAttemptContractDigest,
    });
}
export function createRecurrenceSuccessorProposal(input) {
    const assessment = recurrenceDriftAssessmentSchema.parse(input.assessment);
    if (!assessment.successorRequired) {
        throw new Error("successor proposal requires material recurrence drift");
    }
    return recurrenceSuccessorProposalSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        baseContractDigest: input.baseContractDigest,
        baseVersion: input.baseVersion,
        successorVersion: input.successorVersion,
        reason: input.reason,
        driftAssessmentId: assessment.id,
        state: "PROPOSED",
        returnsOccurrenceTo: assessment.policyReviewRequired ? "policy_review" : "confirmation",
        protectedExecutionAllowedBeforeAcceptance: false,
        activeAcceptedVersionEditedInPlace: false,
    });
}
export function applyRecurrenceDriftToOccurrence(input) {
    const assessment = recurrenceDriftAssessmentSchema.parse(input.assessment);
    if (assessment.relationship === "same" || assessment.relationship === "compatible") {
        return recurrenceApplicationSchema.parse({
            protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
            id: input.id,
            occurrenceId: input.occurrenceId,
            driftAssessmentId: assessment.id,
            state: "REUSE_ACCEPTED_CONTRACT",
            attachedContractDigest: input.acceptedContractDigest,
            successorProposal: null,
            canonicalVerdictState: "ELIGIBLE",
            protectedExecutionAllowed: true,
            activeAcceptedVersionEditedInPlace: false,
            historicalAttemptContractDigest: assessment.historicalAttemptContractDigest,
        });
    }
    const successorProposal = createRecurrenceSuccessorProposal({
        id: `${input.id}:successor`,
        assessment,
        baseContractDigest: input.acceptedContractDigest,
        baseVersion: input.acceptedVersion,
        successorVersion: input.successorVersion,
        reason: input.successorReason ?? "Material recurrence drift requires successor acceptance.",
    });
    return recurrenceApplicationSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        occurrenceId: input.occurrenceId,
        driftAssessmentId: assessment.id,
        state: assessment.policyReviewRequired ? "REQUIRES_POLICY_REVIEW" : "REQUIRES_CONFIRMATION",
        attachedContractDigest: null,
        successorProposal,
        canonicalVerdictState: "UNKNOWN",
        protectedExecutionAllowed: false,
        activeAcceptedVersionEditedInPlace: false,
        historicalAttemptContractDigest: assessment.historicalAttemptContractDigest,
    });
}
